/**
 * Public Kata.fit landing page.
 * The story follows the product's actual daily loop: Coach, plan, execution,
 * nutrition, Dojo accountability, and visible progress.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import EchoBrandMark from '../components/EchoBrandMark';
import { BadgeTotemIcon } from '../components/BadgeTotem';
import './LandingPage.css';

const Check = () => <span className="landing-check" aria-hidden="true">✓</span>;

const TodayPreview = () => (
  <div className="today-preview" aria-label="Preview of the Kata.fit Today screen">
    <div className="preview-topline">
      <div>
        <span className="preview-kicker">TODAY</span>
        <strong>Your day, in one place</strong>
      </div>
      <div className="preview-portrait" aria-hidden="true">
        <span>S</span>
      </div>
    </div>

    <article className="preview-coach-card">
      <div className="preview-card-heading">
        <div className="preview-coach-avatar" aria-hidden="true">W</div>
        <div>
          <span className="preview-card-label">YOUR COACH</span>
          <strong>Warden</strong>
        </div>
        <span className="preview-live">READY</span>
      </div>
      <p>Upper body today. Your first working set is already waiting.</p>
      <div className="preview-actions">
        <span>Start workout</span>
        <span>Log breakfast</span>
      </div>
    </article>

    <div className="preview-section-title">
      <span>YOUR DAY</span>
      <span>2 of 4</span>
    </div>

    <article className="preview-activity preview-activity-active">
      <span className="preview-activity-icon" aria-hidden="true">01</span>
      <div>
        <strong>Upper body strength</strong>
        <span>Bench press · Row · Shoulder press</span>
      </div>
      <span className="preview-state">START</span>
    </article>

    <article className="preview-activity">
      <span className="preview-activity-icon" aria-hidden="true">02</span>
      <div>
        <strong>Meals</strong>
        <span>Protein and calories tracked together</span>
      </div>
      <span className="preview-state preview-state-muted">OPEN</span>
    </article>

    <div className="preview-nutrition">
      <div className="preview-nutrition-heading">
        <strong>Daily nutrition</strong>
        <span>On track</span>
      </div>
      <div className="preview-meter"><span /></div>
      <div className="preview-macros">
        <span>Calories</span><span>Protein</span><span>Carbs</span><span>Fat</span>
      </div>
    </div>
  </div>
);

const LandingPage = ({ onSignIn }) => (
  <div className="landing-page">
    <nav className="landing-nav" aria-label="Kata.fit landing navigation">
      <div className="landing-nav-inner">
        <a className="landing-brand" href="#top" aria-label="Kata.fit home">
          <EchoBrandMark variant="compact" className="landing-nav-mark" decorative />
          <span>Kata<span>.</span>fit</span>
        </a>
        <div className="landing-nav-links">
          <a href="#product">Product</a>
          <a href="#dojo">Dojo</a>
          <a href="#progress">Progress</a>
          <Link to="/pricing">Pricing</Link>
        </div>
        <button className="landing-button landing-button-compact" onClick={onSignIn}>
          Open Kata.fit
        </button>
      </div>
    </nav>

    <main id="top">
      <section className="landing-hero landing-shell">
        <div className="landing-hero-copy">
          <p className="landing-eyebrow"><span /> TRAIN. EAT. ADAPT. REPEAT.</p>
          <h1>One place to do the work—and know what comes next.</h1>
          <p className="landing-hero-lede">
            Kata.fit brings your Coach, plan, workouts, meals, check-ins, and people into one daily view.
          </p>
          <div className="landing-actions">
            <button className="landing-button" onClick={onSignIn}>
              Start with today <span aria-hidden="true">→</span>
            </button>
            <a className="landing-text-link" href="#product">See the daily loop</a>
          </div>
          <ul className="landing-proof-list" aria-label="Core product benefits">
            <li><Check /> A plan you can actually execute</li>
            <li><Check /> Coach guidance in context</li>
            <li><Check /> Training and nutrition together</li>
          </ul>
        </div>
        <div className="landing-hero-product">
          <div className="preview-shadow preview-shadow-one" />
          <div className="preview-shadow preview-shadow-two" />
          <TodayPreview />
        </div>
      </section>

      <section className="landing-product landing-section" id="product">
        <div className="landing-shell">
          <header className="landing-section-heading">
            <p className="landing-eyebrow">THE DAILY LOOP</p>
            <h2>Today, guided by your Coach</h2>
            <p>
              Open the app and get the next useful move—not a dashboard you have to decode. Coach sits at the start of Today with your actual plan and actions.
            </p>
          </header>

          <div className="landing-loop">
            <article className="loop-step loop-step-coach">
              <span className="loop-number">01</span>
              <div>
                <p className="loop-label">COACH</p>
                <h3>Context before advice</h3>
                <p>Your Coach can use your plan, recent training, meals, goals, and feedback to help you decide what matters now.</p>
              </div>
              <div className="coach-note" aria-hidden="true">
                <span className="coach-note-name">WARDEN · TODAY</span>
                <p>Keep the first set controlled. We can adjust the rest from there.</p>
                <span className="coach-note-action">Chat with Coach →</span>
              </div>
            </article>

            <article className="loop-step">
              <span className="loop-number">02</span>
              <div>
                <p className="loop-label">TRAIN</p>
                <h3>Train with the whole session in front of you</h3>
                <p>Log sets, reps, weight, duration, supersets, and warmups. Use rest timers, plate calculations, exercise history, and Coach help without leaving the workout.</p>
              </div>
              <ul className="loop-detail-list">
                <li><Check /> Full session tracking</li>
                <li><Check /> Exercise-level history</li>
                <li><Check /> Timers and plate math</li>
              </ul>
            </article>

            <article className="loop-step">
              <span className="loop-number">03</span>
              <div>
                <p className="loop-label">EAT</p>
                <h3>Meals belong beside training</h3>
                <p>Log foods and recipes, use meal photos when useful, and see calories and macros against the targets that shape your day.</p>
              </div>
              <div className="macro-bars" aria-hidden="true">
                <div><span>Protein</span><i><b style={{ width: '82%' }} /></i></div>
                <div><span>Calories</span><i><b style={{ width: '68%' }} /></i></div>
                <div><span>Carbs</span><i><b style={{ width: '74%' }} /></i></div>
              </div>
            </article>

            <article className="loop-step">
              <span className="loop-number">04</span>
              <div>
                <p className="loop-label">ADAPT</p>
                <h3>Plans that move when life does</h3>
                <p>Workouts, meals, measurements, surveys, and media check-ins share one schedule. Rest and travel changes stay linked to the plan instead of becoming loose notes.</p>
              </div>
              <div className="plan-shift" aria-hidden="true">
                <span><b>MON</b> Strength</span>
                <span className="plan-shift-arrow">→</span>
                <span><b>TUE</b> Travel session</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="landing-section landing-dojo" id="dojo">
        <div className="landing-shell landing-split">
          <div className="landing-copy-block">
            <p className="landing-eyebrow">DOJO</p>
            <h2>Your people, in the work with you</h2>
            <p>
              A Dojo turns accountability into part of Today. Train alongside your crew, see authorized shared activity, celebrate wins, and keep Coach guidance connected to the group.
            </p>
            <ul className="landing-inline-features">
              <li>Shared activity</li>
              <li>Comments & kudos</li>
              <li>Dojo roles</li>
              <li>Privacy controls</li>
            </ul>
          </div>
          <div className="dojo-preview" aria-label="Preview of a Kata.fit Dojo">
            <div className="dojo-preview-head">
              <div>
                <span>FOUNDERS DOJO</span>
                <strong>Training together today</strong>
              </div>
              <span className="dojo-count">4 MEMBERS</span>
            </div>
            <div className="dojo-roster" aria-hidden="true">
              <span className="dojo-avatar dojo-avatar-current">SF</span>
              <span className="dojo-avatar">AM</span>
              <span className="dojo-avatar">JR</span>
              <span className="dojo-avatar">KL</span>
            </div>
            <div className="dojo-feed-item">
              <span className="dojo-feed-avatar">AM</span>
              <div><strong>Morning strength complete</strong><span>Bench press · Row · Shoulder press</span></div>
              <span className="dojo-kudo">KUDOS</span>
            </div>
            <div className="dojo-feed-item">
              <span className="dojo-feed-avatar">JR</span>
              <div><strong>Lunch logged</strong><span>Daily nutrition is on track</span></div>
              <span className="dojo-kudo">VIEW</span>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-progress" id="progress">
        <div className="landing-shell landing-split landing-split-reverse">
          <div className="progress-preview" aria-label="Preview of Kata.fit progress">
            <div className="progress-portrait-demo">
              <div className="portrait-rings" aria-hidden="true"><span>SF</span></div>
              <div>
                <span className="preview-card-label">CONSISTENCY, AT A GLANCE</span>
                <strong>Your progress travels with you</strong>
              </div>
            </div>
            <div className="progress-chart" aria-hidden="true">
              <span style={{ height: '32%' }} /><span style={{ height: '46%' }} /><span style={{ height: '41%' }} />
              <span style={{ height: '62%' }} /><span style={{ height: '70%' }} /><span style={{ height: '76%' }} /><span style={{ height: '88%' }} />
            </div>
            <div className="progress-totem-row">
              <BadgeTotemIcon totemKey="wolf" label="Wolf consistency totem" size={48} />
              <div><span>CONSISTENCY TOTEM</span><strong>Built by showing up</strong></div>
            </div>
          </div>
          <div className="landing-copy-block">
            <p className="landing-eyebrow">PROGRESS</p>
            <h2>Progress you can see before you open a chart</h2>
            <p>
              Your progress portrait carries consistency into your profile and Dojo. Go deeper for body metrics, workout volume, personal records, nutrition, media, and history.
            </p>
            <ul className="landing-inline-features">
              <li>Progress portrait</li>
              <li>Volume & PRs</li>
              <li>Body metrics</li>
              <li>Activity history</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="landing-section landing-depth">
        <div className="landing-shell">
          <header className="landing-section-heading landing-section-heading-left">
            <p className="landing-eyebrow">MORE THAN A LOGGER</p>
            <h2>Built around the feedback loop</h2>
          </header>
          <div className="depth-layout">
            <article className="depth-feature depth-feature-large">
              <span className="depth-index">01 / CHECK IN</span>
              <h3>Media check-ins stay attached to the plan</h3>
              <p>Capture required photos or videos, see whether they were accepted, and rerun available analysis without separating the evidence from its activity.</p>
              <div className="form-frame" aria-hidden="true">
                <span className="form-subject" />
                <span className="form-guide form-guide-one" />
                <span className="form-guide form-guide-two" />
                <b>MEDIA CHECK-IN · READY</b>
              </div>
            </article>
            <div className="depth-stack">
              <article className="depth-feature">
                <span className="depth-index">02 / PLAN</span>
                <h3>One schedule, many kinds of work</h3>
                <p>Training, nutrition, measurements, surveys, and progress media live in the same plan.</p>
              </article>
              <article className="depth-feature">
                <span className="depth-index">03 / CONTROL</span>
                <h3>Your data stays scoped</h3>
                <p>Choose what is private, shared with friends, visible to your Dojo, or public.</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-final">
        <div className="landing-shell landing-final-inner">
          <p className="landing-eyebrow">READY WHEN YOU ARE</p>
          <h2>Start with today.</h2>
          <p>Tracking is free. Pro adds the AI guidance and creation tools when you want them.</p>
          <div className="landing-actions landing-actions-centered">
            <button className="landing-button" onClick={onSignIn}>Open Kata.fit <span aria-hidden="true">→</span></button>
            <Link className="landing-text-link" to="/pricing">Compare plans</Link>
          </div>
        </div>
      </section>
    </main>

    <footer className="landing-footer">
      <div className="landing-shell landing-footer-inner">
        <span className="landing-footer-brand">Kata<span>.</span>fit</span>
        <div className="landing-footer-links">
          <Link to="/pricing">Pricing</Link>
          <Link to="/terms-and-conditions">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/refund">Refunds</Link>
          <Link to="/support">Support</Link>
        </div>
        <p>© {new Date().getFullYear()} Kata.fit</p>
      </div>
    </footer>
  </div>
);

export default LandingPage;
